import { useCallback, useState, useEffect, useMemo } from 'react'
import { useSelector } from 'react-redux'

import { getSelectedPlatform } from '../../../helpers/handlePlatformSelection'

const useDeleteAction = ({
  deletedItemSelector,
  deleteItemIsLoadingSelector,
  deleteItemErrorSelector,
  onDelete,
  onClearDelete,
  onDeleteEnd
}) => {
  const [dataWaitingForDelete, setDataWaitingForDelete] = useState(null)
  const [isDeleteModalOpened, setIsDeleteModalOpened] = useState(false)

  const deleteItemError = useSelector(deleteItemErrorSelector)
  const { id: deletedItemId, wasDeleted } = useSelector(deletedItemSelector)
  const deleteItemIsLoading = useSelector(deleteItemIsLoadingSelector)

  const selectedPlatform = getSelectedPlatform()

  const clearErrorHandler = useCallback(() => {
    onClearDelete && onClearDelete()
  }, [onClearDelete])

  useEffect(() => {
    if (wasDeleted || deleteItemError) {
      setIsDeleteModalOpened(false)
      if (onDeleteEnd) onDeleteEnd(deletedItemId)
      clearErrorHandler()
    }
    // eslint-disable-next-line
  }, [wasDeleted, deleteItemError])

  const handleDelete = useCallback(
    deleteProps => {
      // In case we delete item immediately, we use arguments deleteProps
      // In case we delete item via deleteModal, use saved localState dataWaitingForDelete
      const item = deleteProps.item || dataWaitingForDelete.item
      const platform = deleteProps.platform || dataWaitingForDelete.platform
      const account = deleteProps.account || dataWaitingForDelete.account

      onDelete({
        platform: platform || selectedPlatform,
        account,
        id: item.id
      })
    },
    // eslint-disable-next-line
    [dataWaitingForDelete]
  )

  const handleDeleteRequest = useCallback(
    deleteItemData => {
      const { item } = deleteItemData

      if (item.status === 'archived') {
        // on archived items do not show the modal, just delete
        handleDelete(deleteItemData)
      } else {
        // set delete data that should be used in deleteModal
        setDataWaitingForDelete(deleteItemData)
        setIsDeleteModalOpened(true)
      }
    },
    [handleDelete]
  )

  const handleCloseDeleteModal = useCallback(() => {
    setIsDeleteModalOpened(false)
  }, [])

  return useMemo(
    () => ({
      handleDelete,
      handleDeleteRequest,
      handleCloseDeleteModal,
      isDeleteModalOpened,
      deleteItemIsLoading,
      dataWaitingForDelete
    }),
    [
      handleDelete,
      handleDeleteRequest,
      deleteItemIsLoading,
      handleCloseDeleteModal,
      isDeleteModalOpened,
      dataWaitingForDelete
    ]
  )
}

export default useDeleteAction
